import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './VoiceoverEditor.module.scss';
import { useTranslation } from 'react-i18next';
import { useVoiceoverContext } from '../../context/VoiceoverContext';
import cx from 'classnames';
import VideoService from '../../service/VideoService';
import { ToastNotification } from '../Toast/Toast';
import { CustomButtonTooltip } from '../ButtonTooltip/ButtonTooltip';
import SelectOption from '../SelectOption/SelectOption';
import CloseButton from '../CloseButton/CloseButton';
import { LoadingSpinner } from '../Icon/icons';
import { v4 as uuidv4 } from 'uuid';
import { useVideoProgressContext } from '../../context/VideoProgressContext';
import { INITIAL_VOICEOVER_VOLUME } from '../../constants/voiceovers';
import VolumeControlsCard from '../../features/VideoDetails/components/VolumeControlsCard/VolumeControlsCard';
import ContainerWithScroll from '../ContainerWithScroll/ContainerWithScroll';
import CmsAPI from '../../../common/apis/CmsAPI';
import SwitchList from '../SwitchList/SwitchList';
import { CLIPS_TYPE, MEDIA_TYPES, VOICEOVER_TYPE } from '../../constants/clipsConstants';

const VoiceoverEditor = ({ story, miniSidebar, checkClosing, loadVideoVoiceovers, updateVoiceoversListener }) => {
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();

  const [selectedMicro, setSelectedMicro] = useState({ id: '', name: t('voice_over.editor.not_selected') });
  const [availableAudioDevices, setAvailableAudioDevices] = useState({ id: '', name: t('voice_over.editor.not_selected') });

  const [listMode, setListMode] = useState(VOICEOVER_TYPE);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const optionsRef = useRef(null);
  const storyUpdateStatusRef = useRef(false);
  const scrollContainerRef = useRef(null);


  const {
    voiceovers,
    initialVoiceovers,
    selectedVoiceover,
    updateVoiceovers,
    isVoiceoversChanged,
    voiceoversChanged,
    changeSelectedVoiceover,
    changeVoiceovers,
    closeVoiceover,
    resetVoiceovers,
    generalVoiceoverId,
    isVoiceoverRecording,
    isVoiceoverCountdown,
    isAudioPermissionChecked,
    isAudioPermissionAllowed,
    changeIsAudioPermissionChecked,
    changeIsAudioPermissionAllowed,
    voiceoverVolume,
    changeVoiceoverVolume,
    changeIsVoiceoverRecording,
    updateVoiceoverSeekValue,
  } = useVoiceoverContext();

  const processedVoiceovers = useMemo(() => voiceovers?.map(item => ({ ...item, videoVolume: item.volume, start: item.storyOffset + item.trimStartOffset, trimmedDuration: item.trimEndOffset - item.trimStartOffset })), [voiceovers]);


  const { videoProgress } = useVideoProgressContext();
  const { videoParts, currentPart, resetVolumesInitialValues, handleUpdateVolumes } = useVoiceoverContext();


  const scenesWithVolumes = useMemo(() => videoParts?.filter(item => item.id !== 'intro' && item.id !== 'outro'), [videoParts]);
  const selectedList = useMemo(() => (listMode === CLIPS_TYPE ? scenesWithVolumes : processedVoiceovers), [listMode, scenesWithVolumes, processedVoiceovers]);
  const changedVolumesScenes = useMemo(() => videoParts.filter(item => item.hasOwnProperty('initialValues') && (item.videoVolume !== item.initialValues.videoVolume || (!item?.music || item.music.volume !== item.initialValues.musicVolume))), [videoParts]);

  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const volumeRef = useRef(INITIAL_VOICEOVER_VOLUME);

  const currentVoiceover =
    voiceovers?.find(
      ({ trimStartOffset, trimEndOffset, storyOffset }) =>
        storyOffset + trimStartOffset <= videoProgress?.milliseconds &&
        storyOffset + trimEndOffset >= videoProgress?.milliseconds
    ) || {};

  const handleCloseEditor = async () => {
    if (isVoiceoversChanged) {
      const confirmClose = confirm(t('call_to_action.editor.close_confirm'));
      if (confirmClose) {
        voiceoversChanged(false);
        changeSelectedVoiceover({});
        closeVoiceover();
        resetVoiceovers();
      }
    } else {
      changeSelectedVoiceover({});
      closeVoiceover();
      resetVoiceovers();
    }
  };

  const onReloadEvent = event => {
    const e = event || window.event;
    // Cancel the event
    e.preventDefault();
    if (e) {
      e.returnValue = ''; // Legacy method for cross browser support
    }
    return ''; // Legacy method for cross browser support
  };

  const handleSave = async () => {
    try {
      voiceoversChanged(false);
      setIsLoading(true);

      const segments = [];

      if (isVoiceoversChanged) {
        for (let i = 0; i < voiceovers.length; i++) {
          const currentVoiceover = voiceovers[i];

          if (voiceovers[i].isNew) {
            await new Promise(resolve => setTimeout(resolve, 300));

            const { isNew, id, ...rest } = currentVoiceover;
            const { url, fields } = await VideoService.getVoiceoverUploadUrl(story.id, generalVoiceoverId);

            const voiceoverAudioResponse = await fetch(currentVoiceover.path);
            const audioFile = await voiceoverAudioResponse.blob();
            const voiceoverUrl = await VideoService.uploadVoiceover(audioFile, url, fields);

            segments.push({
              ...rest,
              path: voiceoverUrl,
            });
          } else {
            segments.push(currentVoiceover);
          }
        }

        const payload = { segments };
        await VideoService.updateStoryVoiceovers(story.id, generalVoiceoverId, payload);
        await VideoService.applyStoryVoiceovers(story.id);
      }

      if (changedVolumesScenes.length) {
        const scenesToUpdate = changedVolumesScenes.map(({ id, videoVolume, music }) => ({ id, musicId: music?.id, storyId: story.id, musicVolume: music?.volume, videoVolume }));
        await CmsAPI.bulkUpdateScenes(scenesToUpdate);
        resetVolumesInitialValues();
      }

      ToastNotification({
        message: t('clip_organizer.toast.successfully_saved'),
        iconName: 'success',
      });

      storyUpdateStatusRef.current = true;
    } catch (err) {
      ToastNotification({
        message: t('clip_organizer.toast.error_while_saving'),
      });
      console.log(err);
    }

    setIsLoading(false);
  };

  const startRecording = async () => {
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: selectedMicro.id } });
      const recorder = new MediaRecorder(audioStream);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = event => {
        audioChunksRef.current.push(event.data);
      };

      voiceoversChanged(true);

      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);
        audioChunksRef.current = [];

        const newVoiceover = {
          id: uuidv4(),
          path: audioUrl,
          volume: volumeRef.current,
          trimStartOffset: 0,
          trimEndOffset: endTimeRef.current - startTimeRef.current,
          storyOffset: startTimeRef.current,
          duration: endTimeRef.current - startTimeRef.current,
          isNew: true,
        };

        endTimeRef.current = null;
        startTimeRef.current = null;

        audioStream?.getTracks()?.forEach(track => track.stop());

        changeSelectedVoiceover(newVoiceover);
        updateVoiceovers([...voiceovers, newVoiceover]);
      };

      recorder.start();
    } catch (err) {
      console.log(err);
    }
  };

  const checkUpdatedVoiceovers = () => {
    if (storyUpdateStatusRef.current) {
      updateVoiceoversListener();
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current?.stop();
    }
  };

  const handleVolumeChange = useCallback((id, volume, type) => {
    if (voiceovers?.length > 0) {
      voiceoversChanged(true);
    }

    if (listMode === CLIPS_TYPE) {
      handleUpdateVolumes(id, volume, type);
      return;
    }
    changeVoiceoverVolume(volume);

    changeVoiceovers(id, { volume: volume });
  }, [listMode, voiceovers]);

  const handleSelectItem = useCallback((value, id) => {
    if (listMode === CLIPS_TYPE) {
      updateVoiceoverSeekValue(value / story.duration);
      return;
    }
    const voiceover = voiceovers.find(item => item.id === id);
    voiceover && changeSelectedVoiceover(voiceover);
  }, [voiceovers, updateVoiceoverSeekValue]);

  const toggleMode = (mode) => {
    setListMode(mode);
    scrollContainerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  useEffect(() => {
    if (currentVoiceover?.id && isVoiceoverRecording) {
      endTimeRef.current = currentVoiceover?.storyOffset + currentVoiceover?.trimStartOffset - 1;
      changeIsVoiceoverRecording(false);
    }
  }, [currentVoiceover?.id]);

  useEffect(() => {
    if (volumeRef.current) {
      volumeRef.current = voiceoverVolume;
    }
  }, [voiceoverVolume]);

  useEffect(() => {
    if (isVoiceoverRecording) {
      startTimeRef.current = videoProgress?.milliseconds || 0
      startRecording();
      changeSelectedVoiceover({});

      return;
    }

    const isStartTimeExist = startTimeRef.current !== null;

    const isStuckToNextVoiceover = currentVoiceover && currentVoiceover.storyOffset + currentVoiceover.trimStartOffset < videoProgress?.milliseconds;

    if (isStartTimeExist && !isStuckToNextVoiceover) {
      endTimeRef.current = videoProgress?.milliseconds;
    }

    stopRecording();
  }, [isVoiceoverRecording]);

  useEffect(() => {
    if (checkClosing && !miniSidebar) {
      handleCloseEditor();
    }
  }, [checkClosing]);

  useEffect(() => {
    if (isVoiceoversChanged) {
      window.addEventListener('beforeunload', onReloadEvent);
    }

    return () => window.removeEventListener('beforeunload', onReloadEvent);
  }, [isVoiceoversChanged]);

  useEffect(() => {
    const checkAudioPermission = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        changeIsAudioPermissionAllowed(true);
      } catch (err) {
        console.log(err);
        changeIsAudioPermissionAllowed(false);

        ToastNotification({
          message: `${t('voice_over.editor.device_is_not_available')}`,
        });
      }

      changeIsAudioPermissionChecked(true);
    };

    checkAudioPermission();
    loadVideoVoiceovers();

    return () => {
      voiceoversChanged(false);
      checkUpdatedVoiceovers();
    };
  }, []);

  useEffect(() => {
    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices().catch(e => console.log(e));
      const audioDevices = devices
        .filter(({ kind }) => kind === 'audioinput')
        .map(({ label, deviceId }) => ({ id: deviceId, name: label }));

      if (audioDevices.length) {
        setAvailableAudioDevices(audioDevices);
        setSelectedMicro(audioDevices[0]);
      }
    };

    if (isAudioPermissionAllowed) {
      getDevices();
    }
  }, [isAudioPermissionAllowed]);

  return (
    <>
      <div className={styles.mainContainer}>
        <div className={styles.header}>
          <h2>{t('voice_over.editor.title')}</h2>
          <CloseButton onClose={handleCloseEditor} />
        </div>
        {isAudioPermissionChecked ? (
          <>
            <div ref={optionsRef} className={styles.voiceoverOptions}>
              {isAudioPermissionAllowed ? (
                <SelectOption
                  parentRef={optionsRef}
                  title={t('')}
                  currentlySelected={selectedMicro}
                  options={availableAudioDevices}
                  onSelect={option => setSelectedMicro(option)}
                  currentInputClassName={styles.currentSelect}
                  className={cx(styles.currentSelectText, { [styles.disabled]: isVoiceoverRecording || isVoiceoverCountdown })}
                  selectedIcon="singleSelected"
                  unselectedIcon="singleUnselected"
                />
              ) : (
                <div className={styles.noMicroText}>{t('voice_over.editor.no_micro')}</div>
              )}
              <div className={styles.voiceoverVolumeContainer}>
                <div className={styles.divider} />
                <SwitchList options={Object.entries(MEDIA_TYPES)} activeKey={listMode} onSelect={toggleMode} />
              </div>

              <div className={styles.clipsVolumeContainer}>
                <ContainerWithScroll ref={scrollContainerRef} className={cx(styles.volumes, { [styles.disable]: isVoiceoverCountdown || isVoiceoverRecording })}>
                  {selectedList.length ? selectedList?.map(item => (
                    <VolumeControlsCard
                      key={item.id}
                      item={item}
                      volume={item.videoVolume}
                      music={item.music}
                      isActive={item.id === currentPart?.id || item.id === selectedVoiceover.id}
                      onChange={handleVolumeChange}
                      onSelectItem={handleSelectItem}
                      maxVideoVolume={listMode === CLIPS_TYPE ? 125 : 100}
                    />
                  )) : <span className={styles.emptyListText}>{t('voice_over.editor.empty_voiceovers')}</span>}
                </ContainerWithScroll>

              </div>
            </div>
          </>
        ) : (
          <div className={styles.spinnerContainer}>
            <LoadingSpinner theme="light" />
          </div>
        )}
      </div>
      <div className={styles.btnContainer}>
        <CustomButtonTooltip
          label={t('general.editor.save_changes')}
          variant="primary"
          onClick={handleSave}
          className={styles.saveBtn}
          disabled={(!isVoiceoversChanged && changedVolumesScenes?.length === 0) || isVoiceoverRecording || isVoiceoverCountdown}
          loading={isLoading}
          loadingSpinner
        />
      </div>
    </>
  );
};

export default VoiceoverEditor;
